import { NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NumberSuffixPipe } from '@core/pipes/number-suffix.pipe';
import { PercentagePipe } from '@core/pipes/percentage.pipe';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  imports: [
    MatProgressBarModule,
    NumberSuffixPipe,
    PercentagePipe,
    NgTemplateOutlet,
    MatTooltipModule,
    TranslateModule,
  ],
  selector: 'app-progress-bar',
  styleUrls: ['./progress-bar.component.scss'],
  templateUrl: './progress-bar.component.html',
})
export class ProgressBarComponent implements AfterViewInit, OnDestroy {
  public readonly current = input.required<number>();
  public readonly total = input.required<number | null>();

  public readonly currentColor = input<string>('var(--shikenso-blue-base)');
  public readonly totalColor = input<string>('var(--shikenso-purple-base-border)');
  public readonly height = input<number>(8);
  public readonly minWidth = input<number>(126);
  public readonly width = input<number>();
  public readonly label = input<string>('');
  public readonly labelMinWidth = input<number>(24);
  public readonly progressLabelPosition = input<'left' | 'top' | 'right'>('left');
  public readonly progressLabelMinWidth = input<number>(48);
  public readonly progressLabelDecimalPlaces = input<number>(0);
  public readonly progressLabelFontSize = input<number>(14);
  public readonly progressLabelFontWeight = input<number>(400);
  public readonly isProgressLabelVisible = input<boolean>(true);
  public readonly progressLabelTotalColor = input<string>('var(--shikenso-purple-base)');
  public readonly progressLabelTotalOpacity = input<number>(0.5);
  public readonly progressLabelTotalFontSize = input<number>(this.progressLabelFontSize());
  public readonly progressPrefix = input<string>('');
  public readonly progressSuffix = input<string>('');
  public readonly progressCurrency = input<string | null | undefined>('');
  public readonly isValuePercentage = input<boolean>(false);
  public readonly borderColor = input<string | null>(null);
  public readonly showProgressTooltip = input<boolean>(false);
  public readonly showPercentageTooltip = input<boolean>(false);
  public readonly isProgressLabelPercentage = input<boolean>(false);
  public readonly animate = input<boolean>(true);

  public readonly displayValue = signal<number>(0);
  public readonly progressMultiplier = signal<number>(0.5);

  public readonly calculatedTotal = computed(() => {
    return this.total() ?? 0;
  });

  public readonly progress = computed(() => {
    const total = this.calculatedTotal();

    if (total === 0) {
      return 0;
    }

    return (this.current() / total) * 100;
  });

  public readonly progressPercentage = computed(() =>
    !this.current() && !this.total() ? '' : `${(this.current() === 0 ? 0 : this.progress()).toFixed(2)}%`,
  );

  public readonly tooltipContent = computed(() => {
    const content: string[] = [];

    if (this.showProgressTooltip()) {
      content.push(`${this.current()}/${this.total()}`);
    }

    if (this.showPercentageTooltip()) {
      content.push(`${this.showProgressTooltip() ? `(${this.progressPercentage()})` : this.progressPercentage()}`);
    }

    return this.total()! > 0 ? content.join(' ') : this.translate.instant('shared.notSet');
  });

  private readonly translate = inject(TranslateService);

  @ViewChild('progressHost', { static: false })
  private progressHost?: ElementRef<Element>;

  private resizeObserver?: ResizeObserver;

  constructor() {
    this.createAnimationEffect();
  }

  public ngAfterViewInit(): void {
    if (!this.progressHost?.nativeElement) {
      return;
    }

    const element: Element = this.progressHost.nativeElement;

    this.resizeObserver = new ResizeObserver((entries: Array<ResizeObserverEntry>): void => {
      const [entry] = entries;

      if (!entry) {
        return;
      }

      const widthPx: number = Math.floor(entry.contentRect.width);
      const multiplierPx: number = this.computeProgressMultiplier(widthPx);

      this.progressMultiplier.set(multiplierPx);
    });

    this.resizeObserver.observe(element as Element);
  }

  public ngOnDestroy(): void {
    if (this.resizeObserver && this.progressHost?.nativeElement) {
      this.resizeObserver.unobserve(this.progressHost.nativeElement as Element);
      this.resizeObserver.disconnect();
    }
  }

  private computeProgressMultiplier(widthPx: number): number {
    const stepSizePx = 110;

    if (widthPx <= 0) {
      return 0.5;
    }

    const steps: number = Math.ceil(widthPx / stepSizePx);
    const multiplierPx: number = steps * 0.5;

    return multiplierPx;
  }

  private createAnimationEffect(): void {
    effect(() => {
      const target: number = this.progress();
      const shouldAnimate: boolean = this.animate();

      if (!shouldAnimate) {
        this.displayValue.set(target);

        return;
      }

      this.displayValue.set(0);

      setTimeout(() => {
        this.displayValue.set(target);
      });
    });
  }
}
